/** @file SAMPLE_TILE_GLSL: raster's coverage, cut, depth and texel rules per fragment, in GLSL. */
import { DEPTH_RANGE } from '@zilo/pose3d-runtime-babylon/camera/depthRange';

/** A GLSL float literal. */
const f = (x: number): string => (Number.isInteger(x) ? `${String(x)}.0` : String(x));

/**
 * `sampleTile()` repeats raster's `fillQuad` for the fragment's pixel centre (target pixels, y down: `uHeight -
 * gl_FragCoord.y`): `(s, t)` over the parallelogram `tl + s A + t B` with `coveredAt`'s screen top-left tie rule
 * (outside -> discard), the cut distance `lerp(cut3) > 0` -> discard, the depth `lerp(depth3)` written to
 * `gl_FragDepth` with the camera's mapping (`(distance + depth - near) / (far - near)`, DEPTH_RANGE), and the texel of
 * `sampleNearest` (`floor(s texW)` clamped, flips mirrored) or `sampleLinear` (four clamped neighbours, premultiplied
 * weights) read with `texelFetch`. The GPU's own coverage only has to include these fragments (the quad is grown).
 */
export const SAMPLE_TILE_GLSL = `precision highp float;
flat varying vec4 vGeomA;
flat varying vec4 vGeomB;
flat varying vec3 vDepth;
flat varying vec3 vCut;
flat varying vec4 vRect;
uniform sampler2D sheet;
uniform float nearest;
uniform float uHeight;
vec4 fetchTexel(float u, float v) { return texelFetch(sheet, ivec2(int(u), int(v)), 0); }
vec4 premul(vec4 c) { return vec4(c.rgb * c.a, c.a); }
bool topLeft(float gx, float gy) { return gx > 0.0 || (gx == 0.0 && gy > 0.0); }
bool inRange(float e, float area, bool lo, bool hi) { return (e > 0.0 || (e == 0.0 && lo)) && (e < area || (e == area && hi)); }
float lerp3(vec3 v, float s, float t) { return v.x + s * (v.y - v.x) + t * (v.z - v.x); }
vec4 sampleNearestTile(float s, float t, vec4 r, vec2 fl) {
  float iu = clamp(floor(s * r.z), 0.0, r.z - 1.0);
  float iv = clamp(floor(t * r.w), 0.0, r.w - 1.0);
  return fetchTexel(r.x + (fl.x > 0.5 ? r.z - 1.0 - iu : iu), r.y + (fl.y > 0.5 ? r.w - 1.0 - iv : iv));
}
vec4 sampleLinearTile(float s, float t, vec4 r, vec2 fl) {
  float u = r.x + (fl.x > 0.5 ? r.z - s * r.z : s * r.z) - 0.5;
  float v = r.y + (fl.y > 0.5 ? r.w - t * r.w : t * r.w) - 0.5;
  float u0 = floor(u);
  float v0 = floor(v);
  float fu = u - u0;
  float fv = v - v0;
  float ua = clamp(u0, r.x, r.x + r.z - 1.0);
  float ub = clamp(u0 + 1.0, r.x, r.x + r.z - 1.0);
  float va = clamp(v0, r.y, r.y + r.w - 1.0);
  float vb = clamp(v0 + 1.0, r.y, r.y + r.w - 1.0);
  vec4 acc = premul(fetchTexel(ua, va)) * (1.0 - fu) * (1.0 - fv) + premul(fetchTexel(ub, va)) * fu * (1.0 - fv)
    + premul(fetchTexel(ua, vb)) * (1.0 - fu) * fv + premul(fetchTexel(ub, vb)) * fu * fv;
  return acc.a <= 0.0 ? vec4(0.0) : vec4(acc.rgb / acc.a, acc.a);
}
vec4 sampleTile() {
  vec2 a = vGeomA.zw;
  vec2 b = vGeomB.xy;
  float det = a.x * b.y - a.y * b.x;
  if (!(abs(det) >= 1e-9)) discard;
  float sg = det > 0.0 ? 1.0 : -1.0;
  float area = abs(det);
  vec2 d = vec2(gl_FragCoord.x, uHeight - gl_FragCoord.y) - vGeomA.xy;
  float es = (d.x * b.y - d.y * b.x) * sg;
  float et = (a.x * d.y - a.y * d.x) * sg;
  if (!inRange(es, area, topLeft(sg * b.y, -sg * b.x), topLeft(-sg * b.y, sg * b.x))) discard;
  if (!inRange(et, area, topLeft(-sg * a.y, sg * a.x), topLeft(sg * a.y, -sg * a.x))) discard;
  float s = es / area;
  float t = et / area;
  if (lerp3(vCut, s, t) > 0.0) discard;
  gl_FragDepth = clamp((${f(DEPTH_RANGE.distance)} + lerp3(vDepth, s, t) - ${f(DEPTH_RANGE.near)}) / ${f(DEPTH_RANGE.far - DEPTH_RANGE.near)}, 0.0, 1.0);
  vec4 r = floor(vRect + 0.5);
  return nearest > 0.5 ? sampleNearestTile(s, t, r, vGeomB.zw) : sampleLinearTile(s, t, r, vGeomB.zw);
}
`;
